import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAIStreaming, FileAttachment } from '@/hooks/useAIStreaming';
import { useAIConversations } from '@/hooks/useAIConversations';
import { useUserRole } from '@/hooks/useUserRole';
import { useDefaultLLM } from '@/hooks/useDefaultLLM';
import { Loader2, Send, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { AIAssistantAvatar } from '@/components/AIAssistantAvatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { AIChatSidebar } from '@/components/ai/AIChatSidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { useToast } from '@/hooks/use-toast';
import { FileUploadButton } from '@/components/ai/FileUploadButton';
import { FileAttachmentPreview } from '@/components/ai/FileAttachmentPreview';
import { MessageAttachments } from '@/components/ai/MessageAttachments';
import { FormattedMessage } from '@/components/ai/FormattedMessage';

const MODELS = [
  // OpenAI GPT (Padrão)
  { 
    id: 'openai/gpt-4o', 
    name: 'GPT-4o', 
    description: '🔥 Multimodal e poderoso (Padrão)' 
  },
  { 
    id: 'openai/gpt-4o-mini', 
    name: 'GPT-4o Mini', 
    description: 'Rápido e econômico' 
  },
  { 
    id: 'openai/gpt-4-turbo', 
    name: 'GPT-4 Turbo', 
    description: 'GPT-4 otimizado para velocidade' 
  },
  { 
    id: 'openai/gpt-3.5-turbo', 
    name: 'GPT-3.5 Turbo', 
    description: 'Clássico, rápido e barato' 
  },
  
  // Google Gemini
  { 
    id: 'google/gemini-2.5-flash', 
    name: 'Gemini 2.5 Flash', 
    description: 'Rápido e eficiente' 
  },
  { 
    id: 'google/gemini-2.5-pro', 
    name: 'Gemini 2.5 Pro', 
    description: 'Mais poderoso para tarefas complexas' 
  },
  { 
    id: 'google/gemini-1.5-pro', 
    name: 'Gemini 1.5 Pro', 
    description: 'Contexto muito longo (2M tokens)' 
  },
  
  // Anthropic Claude
  { 
    id: 'anthropic/claude-3.5-sonnet', 
    name: 'Claude 3.5 Sonnet', 
    description: 'Excelente para análise e raciocínio' 
  },
  { 
    id: 'anthropic/claude-3-opus', 
    name: 'Claude 3 Opus', 
    description: 'Mais poderoso da Anthropic' 
  },
  { 
    id: 'anthropic/claude-3-haiku', 
    name: 'Claude 3 Haiku', 
    description: 'Mais rápido e econômico' 
  },
  
  // Meta Llama
  { 
    id: 'meta-llama/llama-3.1-405b-instruct', 
    name: 'Llama 3.1 405B', 
    description: 'Open source mais poderoso' 
  },
  { 
    id: 'meta-llama/llama-3.1-70b-instruct', 
    name: 'Llama 3.1 70B', 
    description: 'Bom custo-benefício' 
  },
];

const PROMPT_TEMPLATES = [
  // Relatórios
  { label: '📊 Relatório Executivo Completo', prompt: 'Gere um relatório executivo completo com todas as estatísticas do sistema, distribuição geográfica, status de validação e alertas prioritários' },
  { label: '📋 Status de Validações Pendentes', prompt: 'Liste todos os formulários pendentes de validação ordenados por prioridade (tempo de espera)' },
  { label: '📇 Relatório de Cobertura de Contatos', prompt: 'Analise a cobertura de contatos por entidade e liste quais entidades precisam urgentemente de contatos' },
  { label: '✅ Análise de Qualidade de Dados', prompt: 'Gere um relatório de qualidade mostrando completude de cada campo e sugerindo prioridades de preenchimento' },
  { label: '⚡ Atividade Recente (7 dias)', prompt: 'Relatório de todas as ações realizadas nos últimos 7 dias com breakdown por usuário e tipo' },
  { label: '🗺️ Distribuição Geográfica Detalhada', prompt: 'Mostre distribuição completa por região e estado com percentuais e top 10 cidades' },
  
  // Análises
  { label: '🔍 Análise Completa de Duplicatas', prompt: 'Analise todas as entidades do tipo Restaurante e identifique duplicatas reais vs unidades de redes. Gere um relatório com recomendações.' },
  { label: '📊 Score de Qualidade Geral', prompt: 'Calcule o score de qualidade de dados do sistema, considerando completude de campos e inconsistências em restaurantes.' },
  { label: '🔗 Candidatos para Mesclagem', prompt: 'Encontre pares de entidades que são candidatas a mesclagem e explique o motivo.' },
  { label: '📈 Análise Regional', prompt: 'Analise a qualidade dos dados de restaurantes no estado de São Paulo' },
  
  // Ações e Sugestões
  { label: '🧹 Sugestões de Limpeza', prompt: 'Identifique os registros com pior qualidade de dados em restaurantes e sugira ações de correção.' },
  { label: '📋 Campos Incompletos', prompt: 'Quais campos estão mais incompletos nos contatos do sistema?' },
  { label: '⚠️ Inconsistências de Formato', prompt: 'Identifique erros de formato em telefones, emails e outros campos' },
  { label: '🎯 Prioridades de Preenchimento', prompt: 'Com base na análise de qualidade, quais são as prioridades de preenchimento de dados?' },
  { label: '🔍 Investigar Entidade Específica', prompt: 'Analise a entidade [NOME] incluindo documentação, contatos, histórico de alterações e status' },
];

export default function AIPlayground() {
  const { role } = useUserRole();
  const { defaultModel, loading: loadingDefaultModel } = useDefaultLLM(role);
  
  const [inputValue, setInputValue] = useState('');
  const [selectedModel, setSelectedModel] = useState('openai/gpt-4o-mini');
  const [enableTools, setEnableTools] = useState(true);
  const [assistantProfile, setAssistantProfile] = useState<any>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const {
    conversations,
    currentConversation,
    messages: dbMessages,
    isLoading: conversationsLoading,
    createConversation,
    loadConversation,
    updateConversationTitle,
    deleteConversation,
    generateConversationTitle,
    groupConversationsByDate,
  } = useAIConversations();

  const { messages, isStreaming, isUploading, sendMessage, clearMessages, setMessages } = useAIStreaming({
    conversationId: currentConversation?.id,
  });

  useEffect(() => {
    loadAssistantProfile();
  }, []);

  // Update selected model when default model loads
  useEffect(() => {
    if (!loadingDefaultModel && defaultModel) {
      setSelectedModel(defaultModel);
    }
  }, [defaultModel, loadingDefaultModel]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    // Load messages from current conversation into the streaming hook
    if (currentConversation && dbMessages.length > 0) {
      setMessages(dbMessages.map(msg => ({
        role: msg.role as 'user' | 'assistant' | 'system',
        content: msg.content,
        timestamp: new Date(msg.created_at),
        attachments: (msg as any).attachments || [],
      })));
    } else if (!currentConversation) {
      clearMessages();
      setAttachedFiles([]);
    }
  }, [currentConversation, dbMessages]);

  const loadAssistantProfile = async () => {
    const { data } = await supabase
      .from('ai_assistant_profile')
      .select('*')
      .eq('is_active', true)
      .single();
    
    if (data) {
      setAssistantProfile(data);
    }
  };

  const handleNewConversation = async () => {
    const title = 'Nova Conversa';
    const conversation = await createConversation(title, selectedModel);
    if (conversation) {
      clearMessages();
      setAttachedFiles([]);
    }
  };

  const handleFileSelect = (files: FileList) => {
    const newFiles = Array.from(files);
    
    // Validation
    const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
    const MAX_FILES = 10;
    
    for (const file of newFiles) {
      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: "Arquivo muito grande",
          description: `O arquivo "${file.name}" excede o limite de 20MB.`,
          variant: "destructive"
        });
        return;
      }
    }
    
    const totalFiles = attachedFiles.length + newFiles.length;
    if (totalFiles > MAX_FILES) {
      toast({
        title: "Muitos arquivos",
        description: `Você pode anexar no máximo ${MAX_FILES} arquivos por mensagem.`,
        variant: "destructive"
      });
      return;
    }
    
    setAttachedFiles(prev => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSelectConversation = async (conversationId: string) => {
    await loadConversation(conversationId);
  };

  const handleSendMessage = async () => {
    if ((!inputValue.trim() && attachedFiles.length === 0) || isStreaming || isUploading) return;
    
    // Create conversation if doesn't exist
    let conversation = currentConversation;
    if (!conversation) {
      const title = generateConversationTitle(inputValue || 'Arquivos anexados');
      conversation = await createConversation(title, selectedModel);
      if (!conversation) return;
    }
    
    const message = inputValue;
    const files = attachedFiles;
    setInputValue('');
    setAttachedFiles([]);
    await sendMessage(message, selectedModel, enableTools, files);
  };

  const handleTemplateClick = (prompt: string) => {
    setInputValue(prompt);
  };

  const groupedConversations = groupConversationsByDate();
  const hasConversations = conversations.length > 0;

  const handleClearData = () => {
    toast({
      title: "Ação não disponível",
      description: "Esta ação não está disponível nesta página.",
      variant: "destructive"
    });
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        {/* Global Navigation Sidebar */}
        <AppSidebar onClearData={handleClearData} />

        {/* Main Content Area with AI Chat Sidebar */}
        <SidebarInset className="flex flex-1 overflow-hidden">
          <div className="flex h-screen w-full">
            {/* AI Conversations Sidebar */}
            <AIChatSidebar
              conversations={groupedConversations}
              currentConversationId={currentConversation?.id || null}
              isCollapsed={sidebarCollapsed}
              onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
              onNewConversation={handleNewConversation}
              onSelectConversation={handleSelectConversation}
              onRenameConversation={updateConversationTitle}
              onDeleteConversation={deleteConversation}
            />

            {/* Chat Area */}
            <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="h-6 w-6 text-primary" />
              <div>
                <h1 className="text-xl font-semibold">
                  {currentConversation?.title || 'Assistente de IA'}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {assistantProfile?.name || 'Análise de Qualidade de Dados'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="tools-mode"
                  checked={enableTools}
                  onCheckedChange={setEnableTools}
                />
                <Label htmlFor="tools-mode" className="text-sm cursor-pointer">
                  Ferramentas
                </Label>
              </div>
              <Select value={selectedModel} onValueChange={setSelectedModel}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODELS.map(model => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <ScrollArea className="flex-1">
          <div className="max-w-4xl mx-auto p-6">
            {conversationsLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            ) : !hasConversations || (messages.length === 0 && !currentConversation) ? (
              <div className="flex flex-col items-center justify-center h-full text-center space-y-6 py-12">
                <AIAssistantAvatar size="lg" />
                <div className="space-y-2">
                  <h2 className="text-2xl font-semibold">
                    {assistantProfile?.name || 'Assistente de IA'}
                  </h2>
                  <p className="text-muted-foreground max-w-md">
                    {assistantProfile?.description || 'Como posso ajudá-lo hoje? Pergunte sobre seus dados, solicite análises ou explore o sistema.'}
                  </p>
                </div>
                
                {/* Templates as suggestions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full max-w-2xl mt-8">
                  {PROMPT_TEMPLATES.slice(0, 4).map((template, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      className="h-auto py-4 px-4 text-left justify-start"
                      onClick={() => handleTemplateClick(template.prompt)}
                    >
                      <span className="text-sm whitespace-normal">{template.label}</span>
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex gap-4 ${
                      message.role === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    {message.role === 'assistant' && (
                      <div className="flex-shrink-0">
                        <AIAssistantAvatar size="sm" />
                      </div>
                    )}
                    <div
                      className={`rounded-lg px-4 py-3 max-w-[80%] ${
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                     >
                       {message.role === 'assistant' ? (
                         <FormattedMessage content={message.content} />
                       ) : (
                         <p className="whitespace-pre-wrap break-words">{message.content}</p>
                       )}
                      {message.attachments && message.attachments.length > 0 && (
                        <MessageAttachments attachments={message.attachments} />
                      )}
                      {message.timestamp && (
                        <p className="text-xs mt-2 opacity-70">
                          {message.timestamp.toLocaleTimeString()}
                        </p>
                      )}
                    </div>
                    {message.role === 'user' && (
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-medium">
                          U
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {isStreaming && (
                  <div className="flex gap-4 justify-start">
                    <div className="flex-shrink-0">
                      <AIAssistantAvatar size="sm" />
                    </div>
                    <div className="rounded-lg px-4 py-3 bg-muted">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div className="border-t p-4">
          <div className="max-w-4xl mx-auto space-y-2">
            {/* File Attachments Preview */}
            {attachedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {attachedFiles.map((file, index) => (
                  <FileAttachmentPreview
                    key={index}
                    file={file}
                    onRemove={() => handleRemoveFile(index)}
                  />
                ))}
              </div>
            )}

            {/* Upload Progress */}
            {isUploading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Enviando arquivos...</span>
              </div>
            )}

            {/* Input Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
              className="flex gap-2"
            >
              <FileUploadButton
                onFileSelect={handleFileSelect}
                disabled={isStreaming || isUploading}
              />
              <Textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Digite sua mensagem..."
                disabled={isStreaming || isUploading}
                className="flex-1 min-h-[44px] max-h-32 resize-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              <Button 
                type="submit" 
                disabled={(!inputValue.trim() && attachedFiles.length === 0) || isStreaming || isUploading}
                size="icon"
                className="shrink-0"
              >
                {isStreaming || isUploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </form>
          </div>
        </div>
            </div>
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}
